"use client";

/**
 * LA PAGE D'UNE AUTOFACTURE : son original, et rien qui permette de la
 * ré-émettre.
 *
 * Pas d'éditeur, pas d'envoi, pas de duplication : le document qui fait foi est
 * le PDF de l'émetteur (voir « src/lib/autofacture.ts »). Seuls sont rappelés,
 * au-dessus, ce que MyFacture en a enregistré — numéro, dates, montants,
 * statut — pour qu'on puisse les comparer d'un coup d'œil au document.
 */

import { ArrowLeft, Download, FileCheck2 } from "lucide-react";
import type { Facture } from "@/types";
import { emetteurAutofacture, urlDuPdfOriginal } from "@/lib/autofacture";

const euros = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
const date = (d?: string | null) => (d ? new Date(d).toLocaleDateString("fr-FR", { timeZone: "UTC" }) : "—");

export function AutofactureOriginal({ facture, onBack }: { facture: Facture; onBack?: () => void }) {
  const url = urlDuPdfOriginal(facture.id);
  const emetteur = emetteurAutofacture(facture.referenceExterne);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="autofacture-original">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {onBack && (
          <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-blue-500 hover:text-blue-400 font-medium">
            <ArrowLeft className="h-4 w-4" /> Factures
          </button>
        )}
        <a
          href={url}
          download
          className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-border text-sm font-medium hover:bg-muted"
        >
          <Download className="h-4 w-4" /> Télécharger l'original
        </a>
      </div>

      <div className="glass-card rounded-xl border border-sky-500/30 bg-sky-500/5 p-4 space-y-3">
        <div className="flex items-start gap-3">
          <FileCheck2 className="h-5 w-5 text-sky-400 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-foreground">
              Autofacture n° {facture.numero} — émise par {emetteur}
            </p>
            <p className="text-muted-foreground mt-0.5">
              Établie par {emetteur} au nom et pour le compte de la société. Le document ci-dessous est l'original : c'est lui qui fait foi.
              MyFacture l'enregistre sans le ré-émettre — il ne s'envoie, ne se duplique ni ne se modifie d'ici.
            </p>
          </div>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
          <div><dt className="text-muted-foreground text-xs">Émise le</dt><dd className="font-medium">{date(facture.dateEmission)}</dd></div>
          <div><dt className="text-muted-foreground text-xs">Échéance</dt><dd className="font-medium">{date(facture.echeance)}</dd></div>
          <div><dt className="text-muted-foreground text-xs">Total HT</dt><dd className="font-medium">{euros(facture.totalHT)}</dd></div>
          <div><dt className="text-muted-foreground text-xs">Total TTC</dt><dd className="font-medium">{euros(facture.totalTTC)}</dd></div>
          <div>
            <dt className="text-muted-foreground text-xs">Statut</dt>
            <dd className="font-medium">
              {facture.statut === "Payée" ? `Payée le ${date(facture.datePaiement)}` : facture.statut === "Envoyée" ? "En attente de paiement" : facture.statut}
            </dd>
          </div>
        </dl>
        {facture.conditions && (
          <p className="text-xs text-muted-foreground whitespace-pre-line border-t border-sky-500/20 pt-2">{facture.conditions}</p>
        )}
      </div>

      <iframe
        src={url}
        title={`Autofacture ${facture.numero} — original`}
        className="w-full h-[75vh] rounded-xl border border-border bg-white"
      />
    </div>
  );
}
